import { createWithEqualityFn } from 'zustand/traditional';
import { persist, createJSONStorage } from 'zustand/middleware';
import { SearchParams } from '@src/types/typesense';
import { fetchSearchPage } from '@src/utils/typesense';
import type { Hit } from 'instantsearch.js';

export type QuestionBankHitsState = {
  currentHitId?: string;
  hits: Hit[];
  params: SearchParams;
  page: number;
  nbPages: number;

  actions: {
    setCurrentHitId: (id: string) => void;
    setSnapshot: (params: SearchParams, hits: Hit[], page: number, nbPages: number) => void;
    fetchNextPage: () => Promise<void>;
    reset: () => void;
  };
};

export const defaults: Omit<QuestionBankHitsState, 'actions'> = {
  hits: [],
  params: {
    query: '*',
    hitsPerPage: 12,
    page: 0,
    filter_by: '',
    sortBy: 'series:asc,paper:asc,question_number:asc',
  },
  page: 0,
  nbPages: 0,
};

const useQuestionBankHits = createWithEqualityFn<QuestionBankHitsState>()(
  persist(
    (set, get) => ({
      ...defaults,

      actions: {
        setCurrentHitId: (id) => set({ currentHitId: id }),

        setSnapshot: (params, hits, page, nbPages) => set({ params, hits, page, nbPages }),

        fetchNextPage: async () => {
          const { params, page, nbPages, hits } = get();
          if (!params || page + 1 >= nbPages) return;

          const nextPage = page + 1;
          const res = await fetchSearchPage({ ...params, page: nextPage });

          set({
            hits: [...hits, ...res.hits],
            page: res.page,
            nbPages: res.nbPages,
          });
        },

        reset: () => {
          set((state) => ({ ...state, ...defaults }), true);
        },
      },
    }),
    {
      name: 'question-bank-hits',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) =>
        Object.fromEntries(
          Object.entries(state).filter(([key]) => !['actions'].includes(key)),
        ) as typeof state,
    },
  ),
  Object.is,
);

export const questionBankHitsActions = useQuestionBankHits.getState().actions;

export const useQuestionBankHitResults = () => useQuestionBankHits((state) => state.hits);

export const useQuestionBankHitParams = () => useQuestionBankHits((state) => state.params);

export const useQuestionBankHitCurrentId = () => useQuestionBankHits((state) => state.currentHitId);
